import React from "react";
import { IGroup, IStore } from "../../../classes/interfaces";
import { formatBRL } from "../../../helpers/currency.helper";
import Icon from "../../../components/ui/Icon";
import Photo from "../../../components/ui/Photo";
import { deliveryFeeLabel, isVariableDeliveryFee } from "../helpers/deliveryFee";
import {
  Wrap,
  Cover,
  Card,
  TopRow,
  Avatar,
  NameBlock,
  Name,
  Tagline,
  StatusPill,
  StatusDot,
  InfoLine,
  MetaRow,
  MetaCard,
  MetaLabel,
  MetaValue,
  ActionsRow,
  OrdersLink,
} from "./StoreHeader.styles";

interface StoreHeaderProps {
  company: IStore;
  groups?: IGroup[];
  tableId?: string;
  onOpenTaxas: () => void;
  /** Cardapio-vitrine (/menu): esconde infos de entrega e "Meus pedidos". */
  isMenu?: boolean;
}

export default function StoreHeader({ company, groups, tableId, onOpenTaxas, isMenu }: StoreHeaderProps) {
  const isOpen = company?.status === "opened";
  const isVariableFee = isVariableDeliveryFee(company);
  // Loja que so retira: prazo e taxa de entrega nao se aplicam e confundiam o
  // cliente, entao dao lugar a um aviso explicito de "Somente retirada".
  const isPickupOnly = company?.hasPickup === true && company?.hasDelivery !== true;
  const tagline = (groups ?? [])
    .slice(0, 3)
    .map((g) => g.name)
    .join(" • ");

  return (
    <Wrap>
      <Cover>
        <Photo src={company.coverImageUrl ?? company.imageUrl} radius={0} objectPosition="center 60%" />
      </Cover>
      <Card>
        <TopRow>
          <Avatar>
            <Photo src={company.imageUrl} radius={14} />
          </Avatar>
          <NameBlock>
            <Name>{company === undefined ? "Carregando..." : company?.name}</Name>
            {tagline && <Tagline>{tagline}</Tagline>}
          </NameBlock>
        </TopRow>

        {tableId ? (
          <InfoLine>
            <Icon name="pin" size={13} />
            Você está na mesa {tableId}
          </InfoLine>
        ) : (
          <>
            <InfoLine>
              <Icon name="pin" size={13} />
              {`${company?.address}, ${company?.number} - ${company?.district} - ${company?.city.name}/${company?.city.state}`}
            </InfoLine>
            <InfoLine>
              <Icon name="phone" size={13} />
              {`${company?.phone}${company?.cellPhone ? " / " + company.cellPhone : ""}`}
            </InfoLine>

            {!isMenu && (
              <MetaRow>
                {isPickupOnly ? (
                  <MetaCard>
                    <MetaLabel>
                      <Icon name="pin" size={13} />
                      Retirada
                    </MetaLabel>
                    <MetaValue>Somente retirada</MetaValue>
                  </MetaCard>
                ) : (
                  <>
                    <MetaCard>
                      <MetaLabel>
                        <Icon name="clock" size={13} />
                        Entrega
                      </MetaLabel>
                      <MetaValue>{`${company.initialDeliveryTime}-${company.finalDeliveryTime} min`}</MetaValue>
                    </MetaCard>
                    <MetaCard $clickable={isVariableFee} onClick={isVariableFee ? onOpenTaxas : undefined}>
                      <MetaLabel>
                        <Icon name="moto" size={13} />
                        Taxa
                      </MetaLabel>
                      <MetaValue>{deliveryFeeLabel(company)}</MetaValue>
                    </MetaCard>
                  </>
                )}
                <MetaCard>
                  <MetaLabel>
                    <Icon name="bag" size={13} />
                    Mínimo
                  </MetaLabel>
                  <MetaValue>
                    {company?.minimumSaleValue > 0 ? formatBRL(company.minimumSaleValue) : "Sem mínimo"}
                  </MetaValue>
                </MetaCard>
              </MetaRow>
            )}
          </>
        )}

        <ActionsRow>
          <StatusPill $open={isOpen}>
            <StatusDot $open={isOpen} />
            {isOpen ? "Aberto agora" : "Fechado"}
          </StatusPill>
          {!isMenu && (
            <OrdersLink to={`/orders/${company.id}`}>
              <Icon name="receipt" size={15} />
              Meus pedidos
            </OrdersLink>
          )}
        </ActionsRow>
      </Card>
    </Wrap>
  );
}
